import { graphql, useFragment } from 'react-relay';
import { useMemo } from 'react';
import type { EventPanelListFragment_event$key } from './__generated__/EventPanelListFragment_event.graphql';
import type { EventPanel$data } from './__generated__/EventPanel.graphql';
import { EventCard } from './EventCard';

const eventFragment = graphql`
  fragment EventPanelListFragment_event on EventConnection {
    edges {
      node {
        id
        happenedAt
        type {
          id
        }
        ...EventCardFragment
      }
    }
  }
`;

interface Props {
  eventFragmentRef: EventPanelListFragment_event$key | undefined;
  eventTypes: EventPanel$data['eventTypes'];
  jobId: string;
  jobType: 'SURGERY' | 'TURNOVER';
}

type EventTypeState = NonNullable<NonNullable<EventPanel$data['eventTypes']>[number]>;

export function EventPanelList({ eventFragmentRef, eventTypes, jobId, jobType }: Props) {
  const eventData = useFragment(eventFragment, eventFragmentRef);

  // NOTE: keep server-provided ordering (backup behavior). Sorting here caused unstable ordering when `order` is null/duplicate.
  const orderedEventTypes = useMemo(() => eventTypes ?? [], [eventTypes]);

  const filteredEventTypes = useMemo(() => orderedEventTypes ?? [], [orderedEventTypes]);

  function isPrimaryEventType(
    eventType: EventTypeState | null | undefined,
  ): eventType is EventTypeState & { category: NonNullable<EventTypeState['category']> } {
    return !!eventType?.category;
  }

  function findRelatedEvent(id: string) {
    return eventData?.edges?.find((event) => event?.node?.type?.id === id);
  }

  function getLoggedTime(eventTypeId: string) {
    const relatedEvent = findRelatedEvent(eventTypeId);
    return relatedEvent?.node?.happenedAt || '';
  }

  function getPrimaryTimeBoundaries(eventType: EventTypeState | null | undefined) {
    const typeEventId = eventType?.id || '';
    const primaryEventTypes = filteredEventTypes.filter((et) => isPrimaryEventType(et));
    const primaryIndex = primaryEventTypes.findIndex((et) => et?.id === typeEventId);

    // Only primary events should be restricted by previous/next timestamps (per RfD baseline).
    const getFirstLoggedTime = (ids: string[]) => ids.map((id) => getLoggedTime(id)).find(Boolean) ?? '';

    const previousPrimaryTime =
      primaryIndex === -1
        ? ''
        : getFirstLoggedTime(
            primaryEventTypes
              .slice(0, primaryIndex)
              .reverse()
              .flatMap((et) => (et?.id ? [et.id] : [])),
          );

    const nextPrimaryTime =
      primaryIndex === -1
        ? ''
        : getFirstLoggedTime(primaryEventTypes.slice(primaryIndex + 1).flatMap((et) => (et?.id ? [et.id] : [])));

    return { previousPrimaryTime, nextPrimaryTime };
  }

  return filteredEventTypes.map((eventType, i) => {
    const typeEventId = eventType?.id || '';
    const { previousPrimaryTime, nextPrimaryTime } = getPrimaryTimeBoundaries(eventType);

    return (
      <EventCard
        key={eventType?.id ?? i}
        name={eventType?.name || ''}
        nameI18nKey={eventType?.nameI18nKey ?? undefined}
        typeEventId={typeEventId}
        jobId={jobId}
        eventRef={findRelatedEvent(typeEventId)?.node || undefined}
        previousEventTime={previousPrimaryTime}
        nextEventTime={nextPrimaryTime}
        enabled={eventType?.enabled ?? true}
        blockedReason={eventType?.blockedReason ?? undefined}
        category={eventType?.category ?? null}
      />
    );
  });
}
